<template>
    <div class="audio-player" @touchstart.stop="" @touchmove.stop="">
        <div class="player-card">
            <div class="progress-bar-wrapper">
                <van-slider
                    class="audio-player-slider"
                    v-model="progressPercent"
                    :min="0"
                    :max="100"
                    @drag-start="onDragStart"
                    @drag-end="onDragEnd"
                    :bar-height="4"
                    :button-size="18"
                    @change="onChange"
                    active-color="#ffa726"
                />
            </div>

            <div class="bottom-row">
                <div class="controls">
                    <button class="control-btn play-btn" @click="togglePlay">
                        <van-icon :name="isPlaying ? 'pause' : 'play'" />
                    </button>
                    <button class="control-btn speed-btn" @click="toggleSpeed" :class="{ active: speed !== 1 }">
                        <span class="speed-label">{{ speed }}x</span>
                    </button>
                </div>
                <div class="time-display">
                    <span class="current-time">{{ formatTime(currentTime) }}</span>
                    <span class="divider">/</span>
                    <span class="total-time">{{ formatTime(duration) }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Slider, Icon } from 'vant'
import audioManager from '@/utils/audioManager'

export default {
    name: 'AudioPlayer',
    components: { VanSlider: Slider, VanIcon: Icon },
    props: {
        audioSrc: {
            type: String,
            required: true,
        },
        autoplay: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isPlaying: false,
            currentTime: 0,
            duration: 0,
            speed: 1,
            speeds: [0.75, 0.9, 1, 1.25],
            speedIndex: 2,
            progressPercent: 0,
            isCurrentPlayer: false, // 是否是当前播放的音频
        }
    },
    mounted() {
        // 监听全局音频管理器事件
        audioManager.addListener(this.onAudioManagerEvent)

        // 从全局管理器获取初始状态
        const currentAudio = audioManager.getCurrentAudio()
        if (currentAudio.src === this.audioSrc && currentAudio.isPlaying) {
            this.isCurrentPlayer = true
            this.isPlaying = true
            this.currentTime = currentAudio.currentTime
            this.duration = currentAudio.duration
        }
    },
    computed: {},
    watch: {
        audioSrc(newVal) {
            if (newVal) {
                this.resetPlayer()
                if (this.autoplay) {
                    this.$nextTick(() => {
                        this.play()
                    })
                }
            }
        },
        currentTime() {
            let _progressPercent = 0
            if (this.duration === 0) _progressPercent = 0
            _progressPercent = (this.currentTime / this.duration) * 100

            if (!this.dragState) {
                this.progressPercent = _progressPercent
            }
        },
    },
    methods: {
        togglePlay() {
            if (this.isPlaying) {
                this.pause()
            } else {
                this.play()
            }
        },
        play() {
            // 使用全局音频管理器播放
            audioManager.play(this, this.audioSrc, this.currentTime)
                .then(() => {
                    this.isCurrentPlayer = true
                    this.isPlaying = true
                })
                .catch((err) => {
                    console.error('播放失败:', err)
                    // this.$toast?.('播放失败')
                })
        },
        pause() {
            audioManager.pause()
            if (this.isCurrentPlayer) {
                this.isPlaying = false
            }
        },
        toggleSpeed() {
            this.speedIndex = (this.speedIndex + 1) % this.speeds.length
            this.speed = this.speeds[this.speedIndex]
            // 使用全局音频管理器设置播放速度
            audioManager.setPlaybackRate(this.speed)
        },
        onDragStart() {
            this.dragState = true
        },
        onDragEnd() {
            if (this.duration > 0) {
                const targetTime = (this.progressPercent / 100) * this.duration
                audioManager.seekTo(targetTime)
            }
            setTimeout(() => {
                this.dragState = false
            }, 100)
        },
        onChange(val) {
            if (!this.dragState && this.duration > 0) {
                const targetTime = (val / 100) * this.duration
                audioManager.seekTo(targetTime)
            }
        },
        formatTime(seconds) {
            if (isNaN(seconds)) return '00:00'
            const mins = Math.floor(seconds / 60)
            const secs = Math.floor(seconds % 60)
            return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
        },
        resetPlayer() {
            this.isPlaying = false
            this.currentTime = 0
            this.duration = 0
            this.speed = 1
            this.speedIndex = 2
            this.isCurrentPlayer = false
        },
        // 全局音频管理器事件监听
        onAudioManagerEvent(event, data) {
            switch (event) {
                case 'play':
                    // 如果是其他组件播放，更新状态
                    if (data.component !== this) {
                        this.isCurrentPlayer = false
                        this.isPlaying = false
                    }
                    break
                case 'pause':
                    if (data.component === this) {
                        this.isPlaying = false
                    }
                    break
                case 'stop':
                    if (data.component === this) {
                        this.isPlaying = false
                        this.currentTime = 0
                    }
                    break
                case 'timeupdate':
                    // 只更新当前播放器的进度
                    if (data.component === this) {
                        this.currentTime = data.currentTime
                        this.duration = data.duration
                    }
                    break
                case 'loadedmetadata':
                    if (data.component === this) {
                        this.duration = data.duration
                    }
                    break
                case 'ended':
                    if (data.component === this) {
                        this.isPlaying = false
                        this.currentTime = 0
                    }
                    break
            }
        },
    },
    beforeDestroy() {
        // 移除事件监听器
        audioManager.removeListener(this.onAudioManagerEvent)
        // 通知全局音频管理器清理
        audioManager.cleanup(this)
    },
}
</script>

<style lang="scss" scoped>
.audio-player {
    margin: 24px;
    max-width: 540px;
    background: #fff;
    border-radius: 12px;
    padding: 18px 20px;
    box-sizing: border-box;
    // border: 1px solid rgba(255, 140, 0, 0.12);
    box-shadow: 0 2px 12px rgba(255, 140, 0, 0.18);
}

.player-card {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.progress-bar-wrapper {
    margin-bottom: 4px;
}

.bottom-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.time-display {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-variant-numeric: tabular-nums;

    .current-time {
        color: #ff8c00;
        font-weight: 600;
    }

    .divider {
        color: #ccc;
        font-weight: 500;
    }

    .total-time {
        color: #999;
    }
}

.controls {
    display: flex;
    align-items: center;
    gap: 8px;
}

.control-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8f8f8;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);

    &:active {
        transform: translateY(0);
    }

    .van-icon {
        color: #666;
    }
}

.speed-btn {
    width: 56px;
    height: 28px;
    border-radius: 12px;

    .speed-label {
        font-size: 14px;
        font-weight: 600;
        color: #999;
        transition: color 0.2s;
    }

    &.active {
        background: rgba(255, 140, 0, 0.08);

        .speed-label {
            color: #ff8c00;
        }
    }
}

.play-btn {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: linear-gradient(135deg, #ff8c00 0%, #ffa726 100%);
    box-shadow: 0 4px 16px rgba(255, 140, 0, 0.3);

    &:hover {
        box-shadow: 0 6px 20px rgba(255, 140, 0, 0.4);
        transform: translateY(-2px);
    }

    .van-icon {
        font-size: 26px;
        color: #fff;
    }
}

audio {
    display: none;
}
.audio-player-slider {
    ::v-deep .van-slider__button {
        border-radius: 50%;
        border: 2px solid #ff8c00;
        box-shadow: 0 2px 10px rgba(255, 140, 0, 0.35);
        box-sizing: border-box;
    }
}
</style>
